import { ChevronDown } from "lucide-react";
import type { LinkTileGridComponent } from "../../types";
import { RuntimeLink } from "../../runtime/RendererRuntime";

/**
 * Two-variant link tile grid.
 *
 *  - `primary` (default) — the classic blue background hero tile with a
 *    centred label and chevron icon. Untouched: existing pages render the
 *    same as before.
 *  - `soft` — quieter editorial card with the title at the top, a per-card
 *    description below (which doubles as SEO copy), and a subtle slate
 *    border that thickens on hover. Switches to a 4-column layout on `lg+`
 *    so a 4-link "pages connexes" row sits on one line; falls back to
 *    2 columns on `sm` and 1 column on mobile.
 */
export function LinkTileGridRenderer({ component }: { component: LinkTileGridComponent }) {
  const { items, variant = "primary", title } = component.content;

  if (variant === "soft") {
    return (
      // Light-blue editorial cards — same palette as the homepage Convertisseur
      // panel (`bg-[#E6F4EA]` + brand-blue title). The container has no
      // bottom margin because the surrounding context (PaginatedActivityGrid
      // injection slot) already controls the spacing below.
      <section>
        {title ? (
          <h3
            className="mb-4 text-xl md:text-2xl font-semibold text-[#00B862]"
            style={{ fontFamily: "var(--font-aleo)" }}
          >
            {title}
          </h3>
        ) : null}
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {items.map(({ label, description, href }) => (
            <RuntimeLink
              key={label}
              href={href}
              className="group flex h-full flex-col rounded-md bg-[#E6F4EA] p-5 ring-1 ring-[#00B862]/15 transition hover:ring-[#00B862]/40 hover:bg-[#d6ecdc]"
            >
              <h4 className="text-base md:text-lg font-semibold text-[#00B862] group-hover:underline">
                {label}
              </h4>
              {description ? (
                <p className="mt-2 text-base leading-relaxed text-[#121212]/80">{description}</p>
              ) : null}
            </RuntimeLink>
          ))}
        </div>
      </section>
    );
  }

  return (
    <section className="grid gap-4 sm:grid-cols-2 md:grid-cols-3">
      {items.map(({ label, href }) => (
        <RuntimeLink
          key={label}
          href={href}
          className="flex h-36 md:h-44 rounded-md bg-[#00B862] p-6 text-white transition hover:bg-[#00935A]"
        >
          <div className="flex h-full w-full flex-col items-center justify-center">
            <span className="grid h-8 w-8 place-items-center rounded-full border-2 border-white/80">
              <ChevronDown className="h-5 w-5" />
            </span>
            <div className="mt-3 text-center text-base xl:text-lg font-semibold line-clamp-2 md:line-clamp-3">
              {label}
            </div>
          </div>
        </RuntimeLink>
      ))}
    </section>
  );
}
